// /compare/pic-time: Pixally vs Pic-Time.
//
// Read the header of honeybook.js first: every rule there applies here. Pic-Time's
// facts, with sources and exact words: docs/geo/compare-gallery-platforms-evidence.md.
// Prints: Pixally sells prints and albums from the gallery, shipped to the US and
// Canada only (Ryan, 2 Oct 2026: on from launch; the /preview gallery section
// says the same). Which plans include it is not settled, so no page names a plan
// for it. The competitor's store stays on the page as its own edge.

export const ROUTE = '/compare/pic-time';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 2, 2026';
export const CHECKED_LABEL = 'Pic-Time facts checked';
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs Pic-Time';
export const SEO_TITLE = 'Pixally vs Pic-Time for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'How Pixally and Pic-Time compare on price, galleries, video, print sales and CRM tools for wedding photographers and videographers, with every Pic-Time fact sourced.';

export const LEDE =
  'Pic-Time is a client gallery and sales platform for photographers: galleries, a print store with more than 30 labs, an album designer and slideshows, connecting to a separate CRM such as HoneyBook or Studio Ninja. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with photo, film and hybrid galleries and print sales built in.';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If print and album sales are a large part of your business, Pic-Time is built for that. If you deliver a lot of film or want the CRM and the delivery in one place, the differences below matter.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The Pic-Time column comes from Pic-Time's own pricing pages, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and Pic-Time compared',
    head: ['', 'Pixally', 'Pic-Time'],
    rows: [
      ['Available', 'From January 1, 2027', 'Now'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects (no gallery storage)', 'Yes: 10 GB of photos and 1 GB of video'],
      ['Plans, billed yearly', 'Basic $33, Pro $58, Studio $99 a month', 'Beginner $7, Professional $21, Advanced $42 a month'],
      ['Photo storage', '200 GB, 1 TB, unlimited', '20 GB, 100 GB, unlimited'],
      ['Video', '50 GB to start on paid plans, then packs from $50 for 50 GB; no limit on film length', '5 GB, 30 GB or 60 GB by plan, up to 4K; more from $10 a month for 100 GB'],
      ['CRM', 'Built in: leads, proposals, contracts, payments, projects', 'None built in; connects to HoneyBook, Studio Ninja, Iris Works, Light Blue and Pixifi'],
      ['Print store and albums', 'Yes: prints and albums, shipped to the US and Canada', 'Yes: 30+ labs, album designer'],
      ['Guest search', 'No', 'Face and selfie search'],
      ['Second shooters and contractors', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', 'Not a CRM'],
    ],
  },
  { type: 'heading', level: 2, text: 'When Pic-Time is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'A choice of print labs: Pic-Time\'s store works with more than 30, and has an album designer. Pixally sells prints and albums too, shipped only to the US and Canada.',
      'It is far cheaper as a gallery: from $7 a month billed yearly, with a free plan.',
      'You already use a CRM it connects to, such as HoneyBook or Studio Ninja, and want to keep it.',
      'Large video libraries can be cheaper per gigabyte: 1 TB of extra video storage is $25 a month billed yearly, where Pixally sells 500 GB for $450 once.',
      'Face and selfie search for guests, slideshows with music, and Lightroom uploads.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you deliver a lot of film, run second shooters and editors, or want your bookings, contracts, payments, edit and delivery in one system.',
  },
  { type: 'heading', level: 3, text: 'Film delivery without a ceiling' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: 'Pic-Time\'s video is a storage allowance by plan, 5 GB to 60 GB, with more as a monthly subscription. Pixally puts no limit on film length, plays films with chapters so a couple can jump to the ceremony or the first dance, and sells delivery in packs (50 GB for $50, 100 GB for $95, 500 GB for $450) that never expire.',
  },
  { type: 'heading', level: 3, text: 'The CRM and the delivery in one place' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: 'Pic-Time has no CRM of its own, so bookings, contracts and payments live in a second system. In Pixally the same project carries the inquiry, proposal, contract, payments, the edit through post-production (Pro and Studio), and the delivered gallery.',
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: "Second shooters get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).",
  },
  { type: 'heading', level: 2, text: 'Switching to Pixally' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Does Pic-Time have a CRM?',
    a: 'No. Pic-Time connects to CRMs including HoneyBook, Studio Ninja, Iris Works, Light Blue and Pixifi.',
  },
  {
    q: 'How much video can I deliver on Pic-Time?',
    a: '5 GB on Beginner, 30 GB on Professional and 60 GB on Advanced, up to 4K. Extra video storage costs $10 a month for 100 GB or $25 for 1 TB, billed yearly.',
  },
  {
    q: 'Can I sell prints with Pixally?',
    a: 'Yes. Couples can order prints and albums from their gallery, shipped to the United States and Canada. Pic-Time works with more than 30 labs.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'Pic-Time pricing', url: 'https://www.pic-time.com/pricing/client-delivery-suite' },
  { label: 'Pic-Time video storage pricing', url: 'https://www.pic-time.com/pricing/video-storage' },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
